import React, { useEffect, useMemo, useRef, useState } from 'react'
import fetch from 'isomorphic-unfetch'
import CONFIG from 'isomorphic-config'
import { Autocomplete } from '@pcln/horizon'
import useTypeAhead from '@/components/TypeAhead/useTypeAhead'
import { useMachine } from '@xstate/react'
import type { LOCATION_SEARCH_TYPE, TypeAheadConfigEndpoint } from '@/types'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useBootstrapData from '@/hooks/useBootstrapData'
import fireTypeaheadClickEvent, {
  fireAutocompleteOpenEvent
} from '@/components/TypeAhead/ga4'
import geoMachine from '../../machines/currentLocationMachine'
import { mapTypeToCode, recentSearchesKey } from './constants'
import { renderDisplayText } from '../TypeAhead/Dropdown/RenderCurrentLocation'
import { cityIdExistsInTop20Filters } from '../TypeAhead/utils'
import { top20Filters } from '../TypeAhead/constants'
import type { CityFilterItem, SelectedTopFilters } from '../Hotels/types'
import { fireFilterBarChipGA4Event } from '../TypeAhead/FilterBar/ga4'

export default function AutocompleteWrapper({
  searchKey,
  ref,
  typeAheadEndpoint = 'hotels',
  onItemSelect,
  initialSelectedItem,
  errorMessage,
  label,
  isMobile,
  showFilterChips = false,
  selectedFilterChipData,
  setSelectedFilterChipData,
  showCurrentLocation = false
}: {
  searchKey: string
  onItemSelect?: (item: LOCATION_SEARCH_TYPE) => void
  ref?: React.Ref<HTMLInputElement>
  initialSelectedItem?: LOCATION_SEARCH_TYPE
  typeAheadEndpoint?: TypeAheadConfigEndpoint
  errorMessage?: string
  label?: string
  isMobile?: boolean
  showFilterChips?: boolean
  selectedFilterChipData?: SelectedTopFilters
  setSelectedFilterChipData?: (
    value: React.SetStateAction<SelectedTopFilters>
  ) => void
  showCurrentLocation?: boolean
}) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn }
  } = useBootstrapData()

  const [current, send] = useMachine(geoMachine, {
    context: {
      error: null,
      cityData: null,
      position: null
    }
  })

  // recent search
  const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
  const [recentStartLocations, recentEndLocations] =
    useRecentSearchesLocalStorage(currentRecentSearchesKey)
  const recentSearchesLocalStorage = searchKey.includes('startLocation')
    ? recentStartLocations
    : recentEndLocations
  const recentSearchesFetched = useRecentSearchesFetched(
    clientGUID,
    appName,
    appVersion,
    signedIn
  )
  const recentSearches =
    typeAheadEndpoint === 'hotels'
      ? recentSearchesFetched.slice(0, 3)
      : recentSearchesLocalStorage.slice(0, 3)

  // recommended search
  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn
  })
  const recommendedSearches = useRecommendations(recommendedCityIds)
  const recentOrRecommended =
    recommendedSearches.length > 0 ? recommendedSearches : recentSearches
  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(
    null
  )

  const typeAheadHorizon = useTypeAhead({
    searchValue: autocompleteValue,
    searchKey,
    onLoaded: () => null,
    debounceTime: 100,
    typeAheadEndpoint
  })

  type geoData = {
    cityID: string
    content: string
    itemName: string
    lat: number
    lon: number
    type: string
  }

  const geoData = current.context?.cityData as geoData | null

  const didFireSelect = useRef(false)
  const [autoKey, setAutoKey] = useState('auto-default')

  /**
   * When geolocation resolves:
   * 1) Force a fresh <Autocomplete> instance by changing its `key`.
   *    The Horizon Autocomplete holds internal state (Downshift useCombobox + local useState),
   *    so a remount guarantees we clear input value, highlighted index, open/close state,
   *    and any pending suggestion state from before geo finished.
   * 2) Programmatically "select" the resolved city so the parent form receives it without
   *    requiring another user action.
   * 3) Guard with a ref so this runs once per resolution.
   *
   * Notes:
   * - Autocomplete manages internal items, selectedItem, overlay open state, and uses
   *   downshift’s useCombobox internally, not fully controlled from above—no single
   *   public “reset” API—hence the reliable `key`-flip remount.
   */

  useEffect(() => {
    if (!showCurrentLocation) return
    if (!current?.matches?.('resolved')) return
    const resolved = current.context?.cityData
    if (!resolved) return
    if (didFireSelect.current) return

    setAutoKey(`geo-${resolved.id ?? resolved.cityID}`)
    didFireSelect.current = true
    onItemSelect?.(resolved)
  }, [current, showCurrentLocation, onItemSelect])

  const { autocompleteData: baseAutocompleteData, listType } = useMemo(() => {
    if (autocompleteValue === null || autocompleteValue === '') {
      if (recentOrRecommended?.length > 0) {
        return {
          autocompleteData: [
            {
              isGroupHeader: true,
              itemName:
                recommendedSearches.length > 0
                  ? 'Recommended Searches'
                  : 'Recent Searches'
            },
            ...recentOrRecommended
          ],
          listType:
            recommendedSearches.length > 0 ? 'recommended' : 'recent_searches'
        }
      }
    }
    if (typeAheadHorizon.items.length > 0) {
      return { autocompleteData: typeAheadHorizon.items, listType: 'typein' }
    }
    return { autocompleteData: [], listType: undefined }
  }, [
    recentOrRecommended,
    typeAheadHorizon,
    autocompleteValue,
    recommendedSearches.length
  ])

  const autocompleteData = useMemo(() => {
    const display = renderDisplayText(current, clientGUID)
    const currentLocationItem = {
      ...geoData,
      content: 'Current Location',
      displayLine1: display,
      itemName: geoData?.itemName || '',
      type: 'GEO'
    }
    return showCurrentLocation && isMobile
      ? [currentLocationItem, ...baseAutocompleteData]
      : [...baseAutocompleteData]
  }, [
    geoData,
    current,
    clientGUID,
    baseAutocompleteData,
    isMobile,
    showCurrentLocation
  ])

  const [filterChipData, setFilterChipData] =
    useState<ReadonlyArray<CityFilterItem>>()
  useEffect(() => {
    if (
      autocompleteData[0] &&
      'cityID' in autocompleteData[0] &&
      cityIdExistsInTop20Filters(autocompleteData[0].cityID)
    ) {
      setFilterChipData(top20Filters[autocompleteData[0].cityID])
    } else {
      setFilterChipData(undefined)
    }
  }, [autocompleteData])

  const handleTypeAheadFeedback = ({
    entered,
    id,
    itemName,
    displayName,
    type,
    selectedPosition,
    skipFeedbackCall
  }: LOCATION_SEARCH_TYPE & {
    selectedPosition: number | null
    skipFeedbackCall?: boolean
  }) => {
    if (skipFeedbackCall) return

    // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
    const url = `${
      CONFIG.client.typeahead.feedback[typeAheadEndpoint]
    }/${id}/${encodeURIComponent(entered ?? 'null')}/${encodeURIComponent(
      itemName || displayName || ''
    )}/${selectedPosition ?? ''}`
    const queryParams = `language=en&locale=en-US&placeType=${mapTypeToCode[type]}`
    const isHotel = typeAheadEndpoint === 'hotels'
    if (isHotel) {
      void fetch(`${url}?${queryParams}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          items: typeAheadHorizon.items,
          apiDuration: typeAheadHorizon.duration,
          source: typeAheadHorizon.source
        })
      })
    } else {
      void fetch(`${url}?${queryParams}`)
    }
  }
  return (
    <Autocomplete
      key={autoKey} // remounts when the location resolves
      inputRef={ref}
      errorMessage={errorMessage}
      label={label}
      forceFocusAfterRender
      autocompleteData={autocompleteData}
      handleInputChange={(inputValue: string) => {
        setAutocompleteValue(inputValue)
      }}
      handleSelectItem={(selectedItem, highlightedIndex) => {
        // Handle current location click
        if (
          'content' in selectedItem &&
          selectedItem.content === 'Current Location' &&
          !geoData
        ) {
          send('REQUEST')
          return
        }

        onItemSelect?.(selectedItem as LOCATION_SEARCH_TYPE)
        fireTypeaheadClickEvent(typeAheadEndpoint, listType)
        if ('isGroupHeader' in selectedItem) return

        handleTypeAheadFeedback({
          ...(selectedItem as LOCATION_SEARCH_TYPE),
          selectedPosition: highlightedIndex || null
        })
      }}
      initialSelectedItem={initialSelectedItem}
      handleOnMenuOpen={() => {
        const updatedListType =
          listType === 'typein' && !autocompleteValue ? undefined : listType
        fireAutocompleteOpenEvent(typeAheadEndpoint, updatedListType)
      }}
      filterChipData={showFilterChips ? filterChipData : undefined}
      handleFilterChipClick={(item, selected) => {
        if (setSelectedFilterChipData) {
          setSelectedFilterChipData((prev: SelectedTopFilters) => {
            const { filterType, code, name } = item as CityFilterItem
            const newFilteredSet = new Set(prev[filterType])
            const gaFilterItem = filterType === 'neighborhoods' ? code : name
            if (selected) {
              newFilteredSet.add(`${filterType}_${code}_${name}`)
              fireFilterBarChipGA4Event(filterType, gaFilterItem, true)
            } else {
              newFilteredSet.delete(`${filterType}_${code}_${name}`)
              fireFilterBarChipGA4Event(filterType, gaFilterItem, false)
            }

            return {
              ...prev,
              [filterType]: newFilteredSet
            }
          })
        }
      }}
      selectedFilterChipData={
        selectedFilterChipData &&
        new Set([
          ...selectedFilterChipData.amenities,
          ...selectedFilterChipData.neighborhoods,
          ...selectedFilterChipData.hotelStarLevel,
          ...selectedFilterChipData.guestRating,
          ...selectedFilterChipData.propertyTypes
        ])
      }
    />
  )
}
